// Pure mapping between the Simple Offer editor's form state and the API payloads
// for V3OffersController#update and V3BudgetDepositsController. Side-effect free
// so the deal-type semantics are unit-testable (O-3412).

export const DEAL_TYPES = {
  FLAT: 'flat',
  DOOR: 'door',
  PLUS: 'plus',
  VERSUS: 'versus',
};

const num = (v) => (v === '' || v === null || v === undefined ? 0 : Number(v));

export const breakevenSet = (breakeven) => num(breakeven) > 0;

// Display the raw numeric form value as a currency amount (e.g. "25,000.00").
// The "$" prefix and "USD" suffix are rendered separately by the input, so this
// returns the bare grouped number. Empty/invalid stays empty so the field can be
// cleared.
export function formatCurrencyInput(value) {
  if (value === '' || value === null || value === undefined) return '';
  const n = Number(value);
  if (Number.isNaN(n)) return '';
  return n.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// Strip a typed currency string back to a bare numeric string for storage —
// drops grouping commas / symbols and keeps a single decimal point.
export function sanitizeCurrencyInput(value) {
  const cleaned = String(value).replace(/[^\d.]/g, '');
  const [whole, ...rest] = cleaned.split('.');
  return rest.length ? `${whole}.${rest.join('')}` : whole;
}

export function buildDealFields({ dealType, guarantee, percentage, breakeven, revenueBase }) {
  const guaranteeAmount = num(guarantee);
  const percentageAmount = num(percentage);
  const revenue_base = revenueBase || 'default';
  switch (dealType) {
    case DEAL_TYPES.FLAT:
      return { dollar_amount: guaranteeAmount, upside_percentage: 0, dollar_or_percentage_operator: 'AND', breakeven_threshold: null, revenue_base };
    case DEAL_TYPES.DOOR:
      return { dollar_amount: 0, upside_percentage: percentageAmount, dollar_or_percentage_operator: 'AND', breakeven_threshold: null, revenue_base };
    case DEAL_TYPES.PLUS:
      return { dollar_amount: guaranteeAmount, upside_percentage: percentageAmount, dollar_or_percentage_operator: 'AND', breakeven_threshold: breakevenSet(breakeven) ? num(breakeven) : null, revenue_base };
    case DEAL_TYPES.VERSUS:
      return { dollar_amount: guaranteeAmount, upside_percentage: percentageAmount, dollar_or_percentage_operator: 'OR', breakeven_threshold: null, revenue_base };
    default:
      throw new Error(`Unknown deal type: ${dealType}`);
  }
}

// Inverse of buildDealFields: derive the deal-type radio value from an existing
// offer's persisted fields, so the simple editor opens on the right card.
export function dealTypeFromOffer({ dollar_amount, upside_percentage, dollar_or_percentage_operator } = {}) {
  const d = Number(dollar_amount) || 0;
  const p = Number(upside_percentage) || 0;
  const op = (dollar_or_percentage_operator || 'AND').toUpperCase();
  if (p > 0 && d === 0) return DEAL_TYPES.DOOR;
  if (op === 'OR') return DEAL_TYPES.VERSUS;
  if (p > 0) return DEAL_TYPES.PLUS;
  return DEAL_TYPES.FLAT;
}

// Builds the PATCH payload for editing an existing offer's deal terms. Unlike
// create, venue/event/pipeline are already set, so we only send deal fields,
// the description, and (when editing the role) the existing performer.
export function buildOfferPatch({ form, performerId }) {
  const v3Offer = {
    ...buildDealFields(form),
    guarantee_notes: form.description || '',
    deal_terms_language: form.dealTermsLanguage || '',
  };
  if (performerId) {
    v3Offer.primary_performer_attributes = { id: performerId, act_type: form.role };
  }
  return { v3_offer: v3Offer };
}

// Builds the V3BudgetBonusesController payload from a Simple-form bonus row.
// Mirrors the V3 bonus model: one amount field whose meaning follows the
// operator — dollar_amount for CASH, upside_percentage for PERCENTAGE — with the
// inactive amount left null.
export function buildBonusParams({ v3BudgetId, v3OfferId, operator, ticketThreshold, amount }) {
  const isCash = operator !== 'PERCENTAGE';
  return {
    v3_budget_bonus: {
      v3_budget_id: v3BudgetId,
      v3_offer_id: v3OfferId,
      ticket_count_requirement: num(ticketThreshold),
      dollar_or_percentage_operator: isCash ? 'CASH' : 'PERCENTAGE',
      dollar_amount: isCash ? num(amount) : null,
      upside_percentage: isCash ? null : num(amount),
    },
  };
}

export function buildDepositParams({ v3BudgetId, v3OfferId, depositType, value, dueDateType, days, specificDate }) {
  return {
    v3_budget_deposit: {
      v3_budget_id: v3BudgetId,
      v3_offer_id: v3OfferId,
      deposit_type: depositType,
      value: num(value),
      due_date_type: dueDateType,
      relative_due_date: dueDateType === 'relative' ? num(days) : null,
      specific_due_date: dueDateType === 'specific' ? specificDate : null,
    },
  };
}
